import { Box, Chip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useMemo, useState } from 'react';
import { useDataProvider, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { GetB2bWithdrawalBulkPayoutInformationEndpoint } from '@Common/types/bo/b2b/withdrawals';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { FormValues } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/utils';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

type PayoutInformationResponse =
  GetB2bWithdrawalBulkPayoutInformationEndpoint['Client']['ResponseBody'];

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const getRowGrossAmount = (
  quantityToWithdraw: number,
  quantityAvailable: number,
  amountAvailable: number,
): number => {
  if (
    !Number.isFinite(quantityToWithdraw) ||
    !Number.isFinite(quantityAvailable) ||
    !Number.isFinite(amountAvailable) ||
    quantityAvailable <= 0
  ) {
    return 0;
  }

  const selectedValue = (quantityToWithdraw / quantityAvailable) * amountAvailable;
  return Math.max(0, Math.min(selectedValue, amountAvailable));
};

export function ConfirmationStep() {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();
  const { control, setValue } = useFormContext<FormValues>();
  const companyId = useWatch<FormValues, 'b2b_company_id'>({
    control,
    name: 'b2b_company_id',
  });
  const rows = useWatch<FormValues, 'bulk_rows'>({
    control,
    name: 'bulk_rows',
    defaultValue: [],
  });
  const [payoutInformation, setPayoutInformation] = useState<PayoutInformationResponse | null>(
    null,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchPayoutInformation = async () => {
      if (!Number.isFinite(companyId)) {
        setPayoutInformation(null);
        setValue('bulk_payout_beneficiary_missing', false);
        return;
      }

      try {
        const { data } = await dataProvider.getOne<PayoutInformationResponse>(
          `${resourceBasePath}withdrawals/bulk/payout_information`,
          {
            id: companyId,
          },
        );

        if (!isCancelled) {
          setPayoutInformation(data);
          setValue(
            'bulk_payout_beneficiary_missing',
            data.target === 'company' && data.beneficiary === null,
          );
        }
      } catch {
        if (!isCancelled) {
          setPayoutInformation(null);
          setValue('bulk_payout_beneficiary_missing', false);
        }
      }
    };

    void fetchPayoutInformation();

    return () => {
      isCancelled = true;
    };
  }, [companyId, dataProvider, resourceBasePath, setValue]);

  const totalGrossAmount = useMemo(
    () =>
      rows.reduce(
        (sum, row) =>
          sum +
          getRowGrossAmount(row.quantity_to_withdraw, row.quantity_available, row.amount_available),
        0,
      ),
    [rows],
  );

  const payoutDetails = useMemo(() => {
    if (payoutInformation === null) {
      return null;
    }
    if (payoutInformation.target === 'employee') {
      return {
        label: 'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.employee_accounts',
        variables: undefined,
      };
    }
    const { beneficiary } = payoutInformation;
    if (beneficiary === null) {
      return {
        label:
          'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.company_account_missing',
        status: 'missing' as const,
      };
    }

    return {
      label: 'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.company_account',
      variables: { iban: beneficiary.iban ?? '', name: beneficiary.name ?? '' },
      status: beneficiary.status === 'pending' ? ('pending' as const) : null,
    };
  }, [payoutInformation]);

  const instrumentName = rows.at(0)?.instrument_name;

  return (
    <Box>
      <TypographyTranslated
        variant="h6"
        sx={{ fontWeight: 700, marginBottom: 2 }}
        label={'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.title'}
      />
      <TypographyTranslated
        label={'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.operation_type'}
      />
      <TypographyTranslated
        label={
          'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.total_gross_amount_withdrawal'
        }
        variables={{ amount: formatAmount(totalGrossAmount) }}
      />
      {instrumentName && <Typography>Instrument: {instrumentName}</Typography>}

      <TypographyTranslated
        label={
          'resources.bo_common.b2b.withdrawals.bulk_create.confirmation.number_of_employees_concerned'
        }
        variables={{ count: String(rows.filter((row) => row.quantity_to_withdraw > 0).length) }}
      />

      {payoutDetails !== null && (
        <>
          <TypographyTranslated
            variant="subtitle2"
            sx={{ fontWeight: 700 }}
            label="resources.bo_common.b2b.withdrawals.bulk_create.confirmation.destination_account"
          />
          <Box display="flex" alignItems="baseline" gap={1}>
            <TypographyTranslated label={payoutDetails.label} variables={payoutDetails.variables} />
            {'status' in payoutDetails && payoutDetails.status && (
              <Chip
                size="small"
                label={translate(
                  `resources.bo_common.b2b.withdrawals.bulk_create.confirmation.status.${payoutDetails.status}`,
                )}
                className={`${classes.statusChip} ${
                  payoutDetails.status === 'pending'
                    ? classes.statusChipPending
                    : classes.statusChipMissing
                }`}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  statusChip: {
    marginTop: theme.spacing(1),
  },
  statusChipPending: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  statusChipMissing: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
}));
